import {
  REQUEST_CATEGORIES,
  REQUEST_STATUSES,
  type StaffRequestSummary,
  type StaffStats,
} from "@hoffman-support/contracts";
import { type FormEvent, useEffect, useState } from "react";
import { staffList, staffStats } from "../staff-api";
import { STATUS_LABELS } from "./RequestList";

export function StaffQueue() {
  const [items, setItems] = useState<StaffRequestSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [stats, setStats] = useState<StaffStats | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [filters, setFilters] = useState({ status: "", category: "", q: "", unassigned: false });

  async function load(next?: string) {
    setState("loading");
    try {
      const page = await staffList({
        status: filters.status || undefined,
        category: filters.category || undefined,
        q: filters.q || undefined,
        unassigned: filters.unassigned ? "true" : undefined,
        cursor: next,
        limit: "50",
      });
      setItems((current) => (next ? [...current, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setState("ready");
    } catch {
      setState("error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
    staffStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  function apply(event: FormEvent) {
    event.preventDefault();
    void load();
  }

  return (
    <section aria-labelledby="queue-heading">
      <h2 id="queue-heading">Support queue</h2>
      {stats ? (
        <p>
          Open {stats.open}, unassigned {stats.unassigned}, awaiting human triage{" "}
          {stats.queuedForHuman}, not yet on the board {stats.unsynced}, callers not yet signed in{" "}
          {stats.unboundProvisional}.
        </p>
      ) : null}
      <p>
        <a href="/staff/requests/new">Raise a request for a caller</a>
      </p>
      <form
        onSubmit={apply}
        style={{
          display: "flex",
          gap: "0.75rem",
          flexWrap: "wrap",
          alignItems: "end",
          marginBottom: "1rem",
        }}
      >
        <label>
          Search
          <br />
          <input
            type="search"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="HOF-000123, subject, work item, email"
          />
        </label>
        <label>
          Status
          <br />
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">Any</option>
            {REQUEST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Category
          <br />
          <select
            value={filters.category}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
          >
            <option value="">Any</option>
            {REQUEST_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={filters.unassigned}
            onChange={(e) => setFilters((f) => ({ ...f, unassigned: e.target.checked }))}
          />{" "}
          Unassigned only
        </label>
        <button type="submit">Apply</button>
      </form>
      {state === "error" ? <p role="alert">The queue could not be loaded.</p> : null}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: "left" }}>
                Reference
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Subject
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Requester
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Status
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Priority
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Assigned
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Triage
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid #ddd" }}>
                <td>
                  <a href={`/staff/requests/${r.publicId}`}>{r.publicId}</a>
                </td>
                <td>
                  {r.subject}
                  {!r.onBoard && r.status !== "withdrawn" ? (
                    <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem", color: "#7a4b00" }}>
                      not on board
                    </span>
                  ) : null}
                </td>
                <td>
                  {r.requester.displayName ?? r.requester.upn ?? "unknown"}
                  {r.provisional ? (
                    <span
                      style={{
                        marginLeft: "0.5rem",
                        fontSize: "0.85rem",
                        color: r.provisional.expired ? "#a4000f" : "#7a4b00",
                      }}
                    >
                      {r.provisional.expired ? "binding expired" : "not yet signed in"}
                    </span>
                  ) : null}
                </td>
                <td>{STATUS_LABELS[r.status]}</td>
                <td>
                  {r.priority ?? ""}
                  {r.pendingBoardPatch ? " (pending)" : ""}
                </td>
                <td>{r.assignedToDisplay ?? ""}</td>
                <td>{r.triageOutcome ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cursor ? (
        <button type="button" onClick={() => void load(cursor)} disabled={state === "loading"}>
          Load more
        </button>
      ) : null}
      <p role="status" aria-live="polite">
        {state === "loading" ? "Loading…" : ""}
      </p>
    </section>
  );
}
